import { Component, OnInit, ViewChild, Inject, AfterViewInit, OnDestroy, ElementRef } from '@angular/core';
import { Observable, BehaviorSubject, Subscription, of, Subject, merge } from 'rxjs';
import { concatMap, map, distinctUntilChanged, tap, catchError, filter } from 'rxjs/operators';
import { PaymentType, PayCustomerItemStatus, PayCustomerAction, PayCustomerActionResultCode } from '../../../shared/enums/smarthub-enums';
import { PayInfo, PayItemInfo } from '../../../services/pay-info.model';
import { TranslateService } from '@ngx-translate/core';
import { GridDataSource } from '../../../shared/models/grid-data-source';
import * as moment from 'moment';
import { PayInfoFiltersService } from '../pay-info-filters/pay-info-filters.service';
import { UIPaymentService } from '../../../services/ui-payment.service';
import { FeatureToggleService } from '../../../shared/services/feature-toggle/feature-toggle.service';
import { UserNotification, UserNotificationCommand } from '../../../shared/components/notification-bar/notification-bar.model';
import { FileExportService } from '../../../shared/services/export-file/export-file.service';
import { EnumTranslatePipe } from '../../../shared/pipes/enumtranslate.pipe';
import { UltiCurrencyPipe } from '../../../shared/pipes/ulticurrency.pipe';
import { SettingsService } from '../../../shared/services/settings/settings.service';
import * as _ from 'lodash';
import { PayInfoFilters } from '../pay-info-filters/pay-info-filters.model';
import { PayInfoView } from '../pay-info-view.model';
import { EntitlementsService } from '../../../entitlements/entitlements.service';
import { PayInfoActionConfirmationInfo } from './pay-info-action-confirmation/pay-info-action-confirmation.component';
import { PayInfoActionResultsInfo } from './pay-info-action-results/pay-info-action-results.component';
import { PayPortionActionResult } from '../../../services/pay-portion-action-info.model';
import { ScrollDispatcher } from '@angular/cdk/scrolling';
import { ActionHistoryService } from '../../../services/action-history.service';
import { ActionHistory } from '../../../services/action-history.model';
import { UltiShortDatePipe } from '../../../shared/pipes/ultishortdate.pipe';
import { HttpErrorResponse } from '@angular/common/http';
import { SortEventData } from './ignite/web-components/sort-event';
import { PaginatorPageEvent, PaginatorPageEventRange } from './ignite/web-components/paginator-page-event';
import { UkgModal, UkgPaginator } from '@ignite/angular';

enum DisplayPaySelectedState {
  Unselected = 0,
  PartiallySelected = 1,
  Selected = 2,
}

export class DisplayPayItemInfo {
  pay: PayInfo;
  payItem: PayItemInfo;

  id: string;
  payItemIndex: number;
  displayName: string;

  payItemSequence: number;
  payItemSequenceCount: number;
}

class ExportedPayItem {
  static headers = {
    name: 'Name (Last, First)',
    employeeId: 'Employee ID',
    payGroup: 'Pay Group',
    payDate: 'Pay Date',
    netAmount: 'Net Amount',
    paymentType: 'Payment Type',
    itemAmount: 'Item Amount',
    checkDDNumber: 'Check/DD #',
    lastKnownStatus: 'Last Known Status'
  };

  static dateFormat = 'MM/DD/YYYY';

  static footer = {
    name: '* Printed on ' + moment().format(ExportedPayItem.dateFormat)
  };

  constructor(
    public name: string,
    public employeeId: string,
    public payGroup: string,
    public payDate: string,
    public netAmount: string,
    public paymentType: string,
    public itemAmount: string,
    public checkDDNumber: string,
    public lastKnownStatus: string) {}
}

class ExportedActionResult extends ExportedPayItem {
  private static readonly _addtionalHeaderColumns = {
    reasonCode: 'Reason Code',
    errorCode: 'Error Code'
  };

  static get headers(): any {
    return { ...ExportedPayItem.headers, ...this._addtionalHeaderColumns };
  }

  constructor(
    public name: string,
    public employeeId: string,
    public payGroup: string,
    public payDate: string,
    public netAmount: string,
    public paymentType: string,
    public itemAmount: string,
    public checkDDNumber: string,
    public lastKnownStatus: string,
    public reasonCode: string,
    public errorCode: string) {
      super(name, employeeId, payGroup, payDate, netAmount, paymentType, itemAmount, checkDDNumber, lastKnownStatus);
    }
}

@Component({
  selector: 'ssg-pay-info-table',
  templateUrl: './pay-info-table.component.html',
  styleUrls: ['./pay-info-table.component.less']
})
export class PayInfoTableComponent implements OnInit, OnDestroy, AfterViewInit {

  public readonly maxStoppableRecords = 10;
  private readonly maxRecordCount = 10000;

  dataSource: GridDataSource<DisplayPayItemInfo>;

  rawData: DisplayPayItemInfo[];
  private _rawDataTable: { [id: string]: DisplayPayItemInfo };
  private _selectedPayTable: { [id: string]: DisplayPaySelectedState };
  private _selectedPayItemTable: { [id: string]: boolean };
  private _selectedPayItemCount: number;
  private _cachedSelection: DisplayPayItemInfo[];

  private _manualFilterUpdate: Subject<any>;

  private _sortSubject: BehaviorSubject<SortEventData>;
  private _pageSubject: BehaviorSubject<PaginatorPageEventRange>;
  selectedPays: DisplayPayItemInfo[];

  private _notification: UserNotification = null;

  private _actionConfirmationInfo: PayInfoActionConfirmationInfo;

  actionResultsInfo: PayInfoActionResultsInfo;

  selectedActionHistories: ActionHistory[];

  allColumns: string[];
  showFilters = false;
  sortKey: SortEventData;

  isWorking = false;
  records: DisplayPayItemInfo[];
  public initialrowsPerPage = 25 as number;
  public rowsOptions = [10, 25, 50, 100];
  private _dataSubscription: Subscription;

  @ViewChild('payInfoTableNotificationBar', { static: true, read: ElementRef }) notificationBarElement: ElementRef;

  @ViewChild('actionConfirmationModal') actionConfirmationModal: UkgModal;
  @ViewChild('actionResultsModal') actionResultsModal: UkgModal;
  @ViewChild('actionHistoryModal') actionHistoryModal: UkgModal;
  loadingData = false;
  actionResultLoaded = false;
  actionHistoryLoaded = false;

  @ViewChild(UkgPaginator)
  private _paginator: UkgPaginator;

  get selectedPayItems(): DisplayPayItemInfo[] {
    if (!this._cachedSelection) {
      const results: DisplayPayItemInfo[] = [];
      for (const id in this._selectedPayItemTable) {
        if (this._selectedPayItemTable.hasOwnProperty(id) && this._selectedPayItemTable[id]) {
          const item = this._rawDataTable[id];
          if (item) {
            results.push(item);
          }
        }
      }

      this._cachedSelection = results;
    }

    return this._cachedSelection;
  }

  get dataCount(): number {
    return (this.rawData || []).length;
  }

  get selectedPayItemCount(): number {
    return this._selectedPayItemCount;
  }

  get showPayInfoPrintButtonAndNotesColumn(): boolean {
    return this._productConfigService.isFeatureEnabled('showPayInfoPrintButtonAndNotesColumn');
  }

  get showStopPaymentButton(): boolean {
    return this._showButton(true);
  }

  get showCancelStopPaymentButton(): boolean {
    return this._showButton(false);
  }

  get showActionHistoryIcon(): boolean {
    return this._entitlementsService.isEntitled(['CuiStopPaymentRequest']);
  }

  get showAttachmentsColumn(): boolean {
    return this._productConfigService.isFeatureEnabled('enableStopPaymentRequest');
  }

  get actionConfirmationInfo() {
    return this._actionConfirmationInfo;
  }

  userMessages = {
    applyFilter: { message: 'EMPLOYEE_PAYS.APPLY_FILTER',
      type: 'user-info',
      severity: 'information',
      persistToStorage: true,
      distinct: true },
    moreRecords: { message: 'EMPLOYEE_PAYS.MORE_RECORDS', type: 'status', severity: 'warning' },
    noMatchingRecords: { message: 'EMPLOYEE_PAYS.NO_MATCHING_RECORDS', type: 'status', severity: 'information' },
    insufficientRights: { message: 'EMPLOYEE_PAYS.STOP_ACTION_RESULT_INSUFFICIENT_RIGHTS', type: 'status', severity: 'important' },
    success: { message: 'EMPLOYEE_PAYS.SUCCESSFUL_QUERY', type: 'status', severity: 'success' },
  };

  notificationSubject: Subject<UserNotificationCommand> = new Subject<UserNotificationCommand>();

  static _getPayItemId(pay: PayInfo, payItemIndex: number) {
    return 'payItem-' + pay.id + '-' + payItemIndex.toString();
  }

  private _showButton(isCancel: boolean): boolean {
    if ( this._productConfigService.isFeatureEnabled('enableStopPaymentRequest' ) ) {
      const entitlement = isCancel ? 'InitiateStopPaymentRequest' : 'InitiateCancelStopPaymentRequest';
        if ( this._productConfigService.isFeatureEnabled('enableEEPDetailedWebAccessRights' ) ) {
           return this._entitlementsService.isEntitled([entitlement]);
        }

        return this._entitlementsService.isEntitled(['CuiStopPaymentRequest']);
    }
  }

  private _setStatusNotification(notif: string, params: any = null) {
    if (this._notification == null) {
      this._notification = new UserNotification(notif, params);
    }
  }

  private _publishNotification(notif: UserNotification): void {
    this.notificationSubject.next(new UserNotificationCommand('add', notif));
  }

  private _clearStatusNotification(): void {
    this.notificationSubject.next(new UserNotificationCommand('clearKey', this._notification));
    this._notification = null;
  }

  // Sorting Event in table
  handleSort(newKey: CustomEvent<SortEventData>): void {
    this._sortSubject.next((newKey as CustomEvent<SortEventData>).detail);
  }

  constructor(
    @Inject(TranslateService) private readonly _translateService: TranslateService,
    @Inject(PayInfoFiltersService) private readonly _filtersService: PayInfoFiltersService,
    @Inject(UIPaymentService) private readonly _uiPaymentService: UIPaymentService,
    @Inject(FeatureToggleService) private readonly _productConfigService: FeatureToggleService,
    @Inject(FileExportService) private readonly _fileExportService: FileExportService,
    @Inject(ActionHistoryService) private readonly _actionHistoryService: ActionHistoryService,
    @Inject(EnumTranslatePipe) private readonly _enumPipe: EnumTranslatePipe,
    @Inject(UltiCurrencyPipe) private readonly _currencyPipe: UltiCurrencyPipe,
    @Inject(UltiShortDatePipe) private readonly _ultiShortDatePipe: UltiShortDatePipe,
    @Inject(ScrollDispatcher) private readonly _scrollDispatcher: ScrollDispatcher,
    private readonly _entitlementsService: EntitlementsService,
    private readonly settings: SettingsService
  ) {
    PayInfoFilters.enableEePayAssemblingStatus = this._productConfigService.isFeatureEnabled('enableEePayAssemblingStatus');
  }

  ngOnInit() {
    this.allColumns = [
      'paySelected',
      'displayName',
      'pay.employee.employeeNumber',
      'pay.integration.payGroup',
      'pay.payUtcDate',
      'pay.netAmount',
      'payItemSelected',
      'payItem.paymentType',
      'payItem.amount',
      'payItem.itemNumber',
      'statusText',
      'attachments'
    ].filter(c => this.showAttachmentsColumn || c !== 'attachments');

    this.records = [];
    this._rawDataTable = {};
    this._resetSelection();

    // (filters + API data -> this._setRawData)
    const apiDataPipeline = this._initApiDataPipeline();

    this._sortSubject = new BehaviorSubject<SortEventData>({ key: null, isAsc: 'none' });
    const sortPipeline = this._sortSubject.pipe(
      distinctUntilChanged((previous: SortEventData, current: SortEventData) =>
        previous.key === current.key && previous.isAsc === current.isAsc));

    this._pageSubject = new BehaviorSubject<PaginatorPageEventRange>({ start: 0, end: this.initialrowsPerPage - 1 });
    const pagePipeline = this._pageSubject.pipe(
      distinctUntilChanged((previous: PaginatorPageEventRange, current: PaginatorPageEventRange) =>
        previous.start === current.start && previous.end === current.end));

    this.dataSource = new GridDataSource(
      apiDataPipeline,
      pagePipeline,
      sortPipeline);

    this.dataSource.processPage = this._processPage;
    this.dataSource.customSort = (data: DisplayPayItemInfo[], sortEvent: SortEventData) => {
      if (sortEvent.key === 'notes') {
        return data;
      } else if (sortEvent.key === 'pay.integration.payGroup') {
        return this.dataSource.defaultSort(data, [{
          key: 'pay.integration.payGroup',
          isAsc: sortEvent.isAsc
        }, {
          key: 'displayName',
          isAsc: sortEvent.isAsc
        }]);
      } else if (sortEvent.key === 'payItem.paymentType') {
        return this.dataSource.defaultSort(data, [{
          key: 'payItem.paymentType',
          isAsc: sortEvent.isAsc
        }, {
          key: 'displayName',
          isAsc: sortEvent.isAsc
        }]);
      }

      return this.dataSource.defaultSort(data, sortEvent);
    };

    this._dataSubscription = this.dataSource.connect().subscribe(data => this.records = data);
  }

  ngOnDestroy() {
    this._dataSubscription.unsubscribe();
    this.records = [];
  }

  ngAfterViewInit() {
    this._publishNotification(new UserNotification('applyFilter', null));
  }

  private _refreshData(success: boolean) {
    this._manualFilterUpdate.next(success);
  }

  private _initApiDataPipeline(): Observable<DisplayPayItemInfo[]> {
    // We need to refresh on updates to either filters or view, but the
    // PayInfoFilters component tracks changes to view and updates filters
    // in response, so we can track filters here to catch both, without
    // worrying about double events.
    // We also track the manualFilterUpdate subject for after applying a Stop.

    this._manualFilterUpdate = new Subject<void>();

    let lastFilters: PayInfoFilters = null;
    const sendManualFilters = function() {
      // Replicate a filter updated event any time we get an update to the
      // manualFilterUpdate subject, just copying the last real filters
      // (which may be null, but we filter out null filters in the first step below).
      return of(lastFilters);
    };
    const manualFiltersUpdate = this._manualFilterUpdate.pipe(sendManualFilters);

    const filtersSource = merge(this._filtersService.getFilters(), manualFiltersUpdate);
    return filtersSource.pipe(
      filter(filters => filters != null),
      tap(() => this._clearStatusNotification()),
      map((filters: PayInfoFilters) => {
        lastFilters = filters;
        if (PayInfoView[filters.view] && !filters.isFormInvalid()) {
          return this._uiPaymentService.getPayInfo(
            filters.view,
            filters.dashboardFilters.payDate,
            filters.payDateTo,
            filters.dashboardFilters.getPaySequences(),
            filters.dashboardFilters.payGroups,
            filters.paymentTypes,
            filters.amount,
            filters.employeeNumbers,
            filters.firstNames,
            filters.lastNames,
            filters.itemNumber,
            filters.statuses,
            1,
            this.maxRecordCount).pipe(
              catchError(error => {
                this._handleError(error);
                return of([] as PayInfo[]);
              })
            );
        } else {
          return of([] as PayInfo[]);
        }
      }),
      concatMap(data => data),
      map(payInfos => {
        payInfos = payInfos || [];
        // Transform the API results (pays) into individual pay items for display in the grid.
        if (payInfos.length >= this.maxRecordCount) {
          this._setStatusNotification('moreRecords', { maxRecords: this.maxRecordCount });
        }
        const results: DisplayPayItemInfo[] = [];
        for (const payInfo of payInfos) {
          if (payInfo.payItems.length === 0) {
            payInfo.payItems.push({ itemNumber: '0',
              amount: 0,
              paymentType: PaymentType.Unknown,
              lastKnownStatus: PayCustomerItemStatus.Unknown,
              hasActionHistory: false
            });
          }
          for (let j = 0; j < payInfo.payItems.length; j++) {
            const displayData = {
              pay: payInfo,
              payItem: payInfo.payItems[j],
              id: PayInfoTableComponent._getPayItemId(payInfo, j),
              payItemIndex: j,
              displayName: this._translateService.instant(
                'EMPLOYEE_PAYS.EMPLOYEE_FULL_NAME', { firstName: payInfo.employee.firstName, lastName: payInfo.employee.lastName }),
              payItemSequence: null,
              payItemSequenceCount: null,
              hasActionHistory: payInfo.payItems[j].hasActionHistory,
              statusText: this._translateService.instant(this.getStatusTextKey(payInfo.payItems[j].lastKnownStatus))
            };

            results.push(displayData);
          }
        }
        return results;
      }),
      tap(data => this._setRawData(data)));
  }

  private _setRawData(value: DisplayPayItemInfo[]) {
    this.rawData = value;
    this._rawDataTable = {};
    for (const data of this.rawData) {
      this._rawDataTable[data.id] = data;
    }
    this._resetSelection();
    if (this._paginator) {
      this._paginator.totalItems = this.dataCount;
    }
    if (this.dataCount === 0) {
      this._setStatusNotification('noMatchingRecords');
    }
    if (this._notification) {
      this._publishNotification(this._notification);
    }
    if (this.notificationBarElement && this.notificationBarElement.nativeElement) {
      const ancestorContainers = this._scrollDispatcher.getAncestorScrollContainers(this.notificationBarElement);
      ancestorContainers.forEach(s => s.scrollTo({ top: 0, left: 0 }));
    }
  }

  private _resetSelection() {
    this._selectedPayTable = {};
    this._selectedPayItemTable = {};
    if (this._selectedPayItemCount !== 0) {
      this._selectedPayItemCount = 0;
      this._handleSelectionChanged();
    }
  }

  private _handleSelectionChanged() {
    this._cachedSelection = null;
  }

  private _processPage(pageData: DisplayPayItemInfo[]) {
    let currentPay: PayInfo = null;
    let currentIndex = 0;
    for (let i = 0; i < pageData.length; i++) {
      pageData[i].payItemSequenceCount = null;
      if (pageData[i].pay === currentPay) {
        if (currentIndex === 0) {
          pageData[i - 1].payItemSequence = currentIndex;
        }
        pageData[i].payItemSequence = ++currentIndex;
      } else {
        pageData[i].payItemSequence = null;
        if (currentIndex > 0) {
          for (let j = 0; j <= currentIndex; j++) {
            pageData[i - j - 1].payItemSequenceCount = currentIndex + 1;
          }
        }
        currentIndex = 0;
      }
      currentPay = pageData[i].pay;
    }
    if (currentIndex > 0) {
      for (let j = 0; j <= currentIndex; j++) {
        pageData[pageData.length - j - 1].payItemSequenceCount = currentIndex + 1;
      }
    }
  }

  private _handleError(error: any) {
    this._setStatusNotification('internalServerError');
  }

  isPaySelected(data: DisplayPayItemInfo): boolean {
    return this._selectedPayTable[data.pay.id] === DisplayPaySelectedState.Selected;
  }

  isPayPartiallySelected(data: DisplayPayItemInfo): boolean {
    return this._selectedPayTable[data.pay.id] === DisplayPaySelectedState.PartiallySelected;
  }

  isPayItemSelected(data: DisplayPayItemInfo): boolean {
    return this._selectedPayItemTable[data.id];
  }

  areAllPaysSelected(): boolean {
    return this.dataCount > 0 && this._selectedPayItemCount === this.dataCount;
  }

  areAnyPaysSelected(): boolean {
    return this._selectedPayItemCount > 0;
  }

  disableStopPaymentButtons(): boolean {
    return this._selectedPayItemCount === 0 || this._selectedPayItemCount > this.maxStoppableRecords;
  }

  clickAllPays(selected: boolean) {
    // This is a work around for IE not supporting indeterminate checkbox state correctly.  If the checkbox is
    // clicked in indeterminate state we force it to select all to behave like other browsers.
    if (!this.areAllPaysSelected() && this.areAnyPaysSelected()) {
      this.selectAllPays(true);
    } else {
      this.selectAllPays(selected);
    }
  }

  // Pagination event in Table
  public onPaginatorChanged(event: unknown) {
    this._pageSubject.next((event as CustomEvent<PaginatorPageEvent>).detail.range);
  }

  selectAllPays(selected: boolean) {
    if (!this.rawData) {
      return;
    }

    const oldCount = this._selectedPayItemCount;
    this._selectedPayItemCount = selected ? this.rawData.length : 0;

    if (this._selectedPayItemCount !== oldCount) {
      const payState = selected ? DisplayPaySelectedState.Selected : DisplayPaySelectedState.Unselected;
      for (const data of this.rawData) {
        this._selectedPayTable[data.pay.id] = payState;
        this._selectedPayItemTable[data.id] = selected;
      }

      this._handleSelectionChanged();
    }
  }

  clickPay(data: DisplayPayItemInfo, selected: boolean) {
    // This is a work around for IE not supporting indeterminate checkbox state correctly.  If the checkbox is
    // clicked in indeterminate state we set selected to true to behave like other browsers.
    if (this.isPayPartiallySelected(data)) {
      this.selectPay(data, true);
    } else {
      this.selectPay(data, selected);
    }
  }

  selectPay(data: DisplayPayItemInfo, selected: boolean) {
    if (selected) {
      if (this._selectedPayTable[data.pay.id] !== DisplayPaySelectedState.Selected) {
        this._selectedPayTable[data.pay.id] = DisplayPaySelectedState.Selected;

        for (let i = 0; i < data.pay.payItems.length; i++) {
          const payItemId = PayInfoTableComponent._getPayItemId(data.pay, i);
          if (!this._selectedPayItemTable[payItemId]) {
            this._selectedPayItemTable[payItemId] = true;
            this._selectedPayItemCount++;
          }
        }

        this._handleSelectionChanged();
      }
    } else {
      if (this._selectedPayTable[data.pay.id] !== DisplayPaySelectedState.Unselected) {
        this._selectedPayTable[data.pay.id] = DisplayPaySelectedState.Unselected;

        for (let i = 0; i < data.pay.payItems.length; i++) {
          const payItemId = PayInfoTableComponent._getPayItemId(data.pay, i);
          if (this._selectedPayItemTable[payItemId]) {
            this._selectedPayItemTable[payItemId] = false;
            this._selectedPayItemCount--;
          }
        }

        this._handleSelectionChanged();
      }
    }
  }

  selectPayItem(data: DisplayPayItemInfo, selected: boolean) {
    let changed = false;

    if (selected) {
      if (!this._selectedPayItemTable[data.id]) {
        this._selectedPayItemTable[data.id] = true;
        this._selectedPayItemCount++;

        changed = true;
      }
    } else {
      if (this._selectedPayItemTable[data.id]) {
        this._selectedPayItemTable[data.id] = false;
        this._selectedPayItemCount--;

        changed = true;
      }
    }

    if (changed) {
      let selectedCount = 0;
      for (let i = 0; i < data.pay.payItems.length; i++) {
        if (this._selectedPayItemTable[PayInfoTableComponent._getPayItemId(data.pay, i)]) {
          selectedCount++;
        }
      }
      this._selectedPayTable[data.pay.id] = selectedCount === data.pay.payItems.length
        ? DisplayPaySelectedState.Selected
        : (selectedCount > 0
          ? DisplayPaySelectedState.PartiallySelected
          : DisplayPaySelectedState.Unselected);

      this._handleSelectionChanged();
    }
  }

  isSubsequentPayRow(row: DisplayPayItemInfo): boolean {
    return row.payItemSequence !== null && row.payItemSequence > 0;
  }

  payHasMultipleItems(pay: PayInfo): boolean {
    return pay.payItems.length > 1;
  }

  showPayItems(pay: PayInfo): boolean {
    return pay.payItems.length > 1 || pay.payItems[0].amount < pay.netAmount;
  }

  getPaymentTypeTextKey(type: PaymentType): string {
    switch (type) {
      case PaymentType.Check:
        return 'EMPLOYEE_PAYS.PAYMENT_TYPE_CHECK';
      case PaymentType.DirectDeposit:
        return 'EMPLOYEE_PAYS.PAYMENT_TYPE_DIRECT_DEPOSIT';
      default:
        return 'EMPLOYEE_PAYS.PAYMENT_TYPE_UNKNOWN';
    }
  }

  getCheckDocumentNumber(info: PayItemInfo, docNum: string): string {
    switch (info.paymentType) {
      case PaymentType.Check:
        return docNum;
      case PaymentType.DirectDeposit:
        return `${docNum}\n${info.maskedAccountNumber}`;
      default:
        return '';
    }
  }

  isStatusError(status: PayCustomerItemStatus): boolean {
    switch (status) {
      case PayCustomerItemStatus.PayReturned:
      case PayCustomerItemStatus.PayWithCannotCollectFlag:
      case PayCustomerItemStatus.PayWithCannotPayFlag:
      case PayCustomerItemStatus.PayDeleted:
      case PayCustomerItemStatus.PayReversed:
      case PayCustomerItemStatus.PayRejected:
      case PayCustomerItemStatus.PayCanceledAfterPaid:
      case PayCustomerItemStatus.PayCanceledBeforePaid:
      case PayCustomerItemStatus.PayStopRequested:
      case PayCustomerItemStatus.PayStopInProgress:
        return true;
      case PayCustomerItemStatus.Unknown:
      default:
        return false;
    }
  }

  isStatusWarning(status: PayCustomerItemStatus): boolean {
    switch (status) {
      case PayCustomerItemStatus.CollectionReturned:
        return true;
      default:
        return false;
    }
  }

  getStatusTextKey(status: PayCustomerItemStatus): string {
    return PayInfoFilters.getStatusTextKey(status);
  }

  getStatusDescriptionKey(status: PayCustomerItemStatus): string {
    return PayInfoFilters.getStatusDescriptionKey(status);
  }

  exportSelectedPays(): void {
    if (this.isWorking) {
      return;
    }

    // This appears to be a bug introduced in the story that added the export feature:
    // the grid sort is replaced by a unique sort that is used here, and only here.
    // I believe the following construct should be replaced with:

    // const items = this._getSelectedPaysAndInfos();

    // However, this would change (correct?) the existing behavior, so I'm not making this change
    // at this time. It's worth considering for the future, though.
    // -Abby Hill, 4/6/2022
    let items;
    if (!GridDataSource.isEmptySort(this._sortSubject.value)) {
      items = this._sortSubject.value.isAsc
        ? _.orderBy(this.selectedPayItems, [this._sortSubject.value.key])
        : _.orderBy(this.selectedPayItems, [this._sortSubject.value.key], ['desc']);
    } else {
      items = this.selectedPayItems;
    }

    const exportedItems: ExportedPayItem[] = items.map(p =>
      new ExportedPayItem(
        p.pay.employee.lastName + ', ' + p.pay.employee.firstName,
        p.pay.employee.employeeNumber,
        p.pay.integration.payGroup,
        moment.utc(p.pay.payUtcDate).format(ExportedPayItem.dateFormat),
        this._currencyPipe.transform(p.pay.netAmount.toString(), '1.02', undefined, true),
        this._enumPipe.transform(p.payItem.paymentType, 'PaymentType'),
        this._currencyPipe.transform(p.payItem.amount.toString(), '1.02', undefined, true),
        p.payItem.paymentType === PaymentType.Check ? p.pay.documentNumber : `${p.pay.documentNumber}/${p.payItem.maskedAccountNumber}`,
        this._translateService.instant(this.getStatusTextKey(p.payItem.lastKnownStatus))
      ));
    this._fileExportService.exportAsCsvFile(
      exportedItems,
      'ViewPays_' + this.settings.alias + '_' + moment().format('YYYYMMDDhhmmss'),
      ExportedPayItem.headers,
      ExportedPayItem.footer);
  }

  private _getSelectedPaysAndInfos() {
    let selectedItems: DisplayPayItemInfo[] = [];
    if (!GridDataSource.isEmptySort(this._sortSubject.value)) {
      selectedItems = this.dataSource.customSort(this.selectedPayItems, this._sortSubject.value);
    } else {
      // This could probably be replaced by:

      // selectedItems = this.selectedPayItems;

      // However, this story isn't about export, so I'm not making this change at this time.
      // -Abby Hill, 4/6/2022
      for (const data of this.rawData) {
        if (this._selectedPayItemTable.hasOwnProperty(data.id) && this._selectedPayItemTable[data.id]) {
          selectedItems.push(data);
        }
      }
    }
    return selectedItems;
  }

  stopPayment(): void {
    this._showActionConfirmation(PayCustomerAction.Stop);
  }

  cancelStopPayment(): void {
    this._showActionConfirmation(PayCustomerAction.CancelStop);
  }

  onSubmit(action): void {
    this.actionConfirmationModal.dismiss();
    this._performConfirmedAction(this.selectedPays);
  }

  private _showActionConfirmation(action: PayCustomerAction) {
    if (this.isWorking) {
      return;
    }

    this.selectedPays = this._getSelectedPaysAndInfos();
    this._actionConfirmationInfo = new PayInfoActionConfirmationInfo( action, this.selectedPays);
    this.loadingData = true;
    this.actionConfirmationModal.present();
  }

  private _performConfirmedAction(selectedPays: DisplayPayItemInfo[]) {
    if (this.isWorking) {
      return;
    }

    this.isWorking = true;
    const email = this.settings.email;
    this._uiPaymentService
      .performAction(this._actionConfirmationInfo.action, this._actionConfirmationInfo.getPortionInfos(), email)
      .subscribe({
        next: results => {
          this._showActionResults(selectedPays, results);
        },
        error: (error: HttpErrorResponse) => {
          switch (error.status) {
            case 403:
              this._setStatusNotification('insufficientRights');
              break;
            default:
              this._setStatusNotification('internalServerError');
          }
          this._publishNotification(this._notification);
        },
        complete: () => {
          this.isWorking = false;
          this._refreshData(true);
        }
      });
  }

  private _showActionResults(selectedPays: DisplayPayItemInfo[], actionResults: PayPortionActionResult[]) {
    if (actionResults) {
      for (const selectedPay of selectedPays) {
        const actionResult = actionResults.find(result =>
          String(result.payId || '').toLowerCase() === String(selectedPay.pay.id || '').toLowerCase()
            && result.type === selectedPay.payItem.paymentType
            && result.itemNo === selectedPay.payItem.itemNumber);

        if (actionResult &&
          actionResult.status !== undefined &&
          actionResult.resultCode !== PayCustomerActionResultCode.NotFoundOrInsufficientAccessRights) {
          selectedPay.payItem.lastKnownStatus = actionResult.status;
        }
      }

      this.actionResultsInfo = new PayInfoActionResultsInfo(
        actionResults,
        selectedPays,
        this._actionConfirmationInfo.action,
        this._actionConfirmationInfo.getPortionInfos(),
        this._productConfigService);

      this.actionResultLoaded = true;
      this.actionResultsModal.present();
    }
  }

  exportActionResults() {
    const allResults = this.actionResultsInfo.successfulItems.concat(this.actionResultsInfo.unsuccessfulItems);
    const exportedActionResults: ExportedActionResult[] = allResults.map(r =>
      new ExportedActionResult(
        `${r.pay.employee.lastName}, ${r.pay.employee.firstName}`,
        r.pay.employee.employeeNumber,
        r.pay.integration.payGroup,
        moment.utc(r.pay.payUtcDate).format(ExportedPayItem.dateFormat),
        this._currencyPipe.transform(r.pay.netAmount.toString(), '1.02', undefined, true),
        this._enumPipe.transform(r.item.paymentType, 'PaymentType'),
        this._currencyPipe.transform(r.item.amount.toString(), '1.02', undefined, true),
        r.item.paymentType === PaymentType.Check ? r.pay.documentNumber : `${r.pay.documentNumber}/${r.item.maskedAccountNumber}`,
        this._translateService.instant(this.getStatusTextKey(r.item.lastKnownStatus)),
        this._translateService.instant(`BANK_NOTIFICATION_CODE.${r.reasonCode}`),
        r.resultCode === PayCustomerActionResultCode.Success ? '' : this._translateService.instant(r.errorKey)
      ));

    this._fileExportService.exportAsCsvFile(
      exportedActionResults,
      `${(this.actionResultsInfo.action === PayCustomerAction.Stop ?
        'StopPaysConf_' :
        'CancelStopPaysConf_')}${this.settings.alias}_${moment().format('YYYYMMDDhhmmss')}`,
      ExportedActionResult.headers,
      ExportedActionResult.footer);

    this.actionResultsModal.dismiss();
  }

  showActionHistory(pay: DisplayPayItemInfo) {
    if (this.isWorking) {
      return;
    }
    this.selectedActionHistories = null;
    this._actionHistoryService.getActionHistory([pay.pay.id])
      .subscribe(histories => {
        this.selectedActionHistories = histories
          .filter(h => h.itemNo === pay.payItem.itemNumber)
          .sort((a, b) => (a.actionUtcDateTime > b.actionUtcDateTime) ? 1 : (a.actionUtcDateTime < b.actionUtcDateTime ? -1 : 0));

          this.actionHistoryLoaded = true;
          this.actionHistoryModal.present();
        });
  }

  translateActionHistory(actionHistory: ActionHistory): any {
    return {
      employeeName: actionHistory.employeeName,
      paymentType: this._translateService.instant(this.getPaymentTypeTextKey(actionHistory.portion)),
      toStatus: this._translateService.instant(actionHistory.toStateKey),
      fromStatus: this._translateService.instant(actionHistory.fromStateKey),
      comment: actionHistory.text,
      user: actionHistory.user,
      bankReasonCode: actionHistory.bankReasonCode,
      bankReasonDescription: this._translateService.instant(`BANK_NOTIFICATION_CODE.${actionHistory.bankReasonCode}`),
      actionUtcDateTime: this._ultiShortDatePipe.transform(actionHistory.actionUtcDateTime, 'LLL'),
      amount: this._currencyPipe.transform(actionHistory.amount, '1.02', undefined, true),
      referenceNumber: actionHistory.referenceNumber
    };
  }
}
